import { ApplicationInsights, type ICustomProperties, SeverityLevel } from '@microsoft/applicationinsights-web';
import { useAppStore } from '@/stores/appStore';
import { getOttoEnv, getOttoRegion } from '@/services/envContext';
import { getBuildInfo } from '@/utils/system';

/**
 * Centralized telemetry service wrapping Azure Application Insights.
 * Singleton — call `telemetryService.initialize()` once at app boot.
 *
 * Runtime gating:
 *   - `telemetryEnabled` (appStore)   — gates trackEvent / trackPageView / trackMetric / trackTrace
 *   - `crashReportsEnabled` (appStore) — gates trackException
 *
 * These are read on every call rather than cached at init so toggling
 * the Settings → Privacy switches takes effect immediately without
 * requiring a reload or re-subscribe. Telemetry is suppressed until
 * appStore persist hydration completes — fail closed during bootstrap
 * so a user's prior opt-out isn't leaked by the in-memory default.
 */

interface TelemetryConfig {
  connectionString: string;
  enabled?: boolean;
  samplingPercentage?: number;
  /** Deployment environment tag (e.g. 'canary', 'production', 'stage'). Attached to all telemetry. */
  environment?: string;
}

const PII_PATTERNS = [
  /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, // emails
  /Bearer\s+[A-Za-z0-9\-._~+/]+=*/g, // bearer tokens
  /eyJ[A-Za-z0-9\-_=]+\.eyJ[A-Za-z0-9\-_=]+\.[A-Za-z0-9\-_.+/=]*/g, // JWTs
  /(?:key|secret|password|token|credential|connectionstring)["']?\s*[:=]\s*["']?[^\s"',}{)]+/gi, // key-value secrets
];

function sanitizeValue(value: string): string {
  let sanitized = value;
  for (const pattern of PII_PATTERNS) {
    sanitized = sanitized.replace(pattern, '[REDACTED]');
  }
  return sanitized;
}

function sanitizeProperties(
  properties?: Record<string, unknown>
): Record<string, string> | undefined {
  if (!properties) return undefined;
  const sanitized: Record<string, string> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (value === undefined || value === null) continue;
    const strValue = typeof value === 'string' ? value : JSON.stringify(value);
    sanitized[key] = sanitizeValue(strValue);
  }
  return sanitized;
}

// Track when the appStore has finished rehydrating so we don't leak
// telemetry events from the bootstrap window where the in-memory
// `telemetryEnabled` default ('true') is still showing instead of the
// user's persisted opt-out. See useAppStore.persist.* docs.
//
// `persist` is optional-guarded so this module survives tests that mock
// `@/stores/appStore` wholesale (no persist middleware). When the persist
// API is absent we treat the store as hydrated — the real app always has
// it, so this only relaxes the gate in mocked unit tests.
const appStorePersist = (useAppStore as { persist?: typeof useAppStore.persist }).persist;
let appStoreHydrated = appStorePersist?.hasHydrated() ?? true;
appStorePersist?.onFinishHydration(() => {
  appStoreHydrated = true;
});

class TelemetryService {
  private appInsights: ApplicationInsights | null = null;
  private initialized = false;
  private enabled = false;
  private _environment = 'unknown';

  /**
   * Reads the user-preference gate for non-exception telemetry. Crash
   * reports use {@link userCrashReportsAllowed} instead so the user can
   * keep diagnostic reporting while opting out of usage tracking.
   */
  private userTelemetryAllowed(): boolean {
    // During the bootstrap window (between module load and persist
    // rehydration) the store still shows the in-memory default
    // (`telemetryEnabled: true`), which can leak telemetry for users who
    // previously opted out. Suppress until we know the persisted value.
    if (!appStoreHydrated) return false;
    try {
      return useAppStore.getState().telemetryEnabled !== false;
    } catch {
      // If the store ever throws after hydration, fail open — losing
      // telemetry for an active user is worse than the rare double-check.
      return true;
    }
  }

  private userCrashReportsAllowed(): boolean {
    if (!appStoreHydrated) return false;
    try {
      return useAppStore.getState().crashReportsEnabled !== false;
    } catch {
      return true;
    }
  }

  initialize(config: TelemetryConfig): void {
    if (this.initialized) return;

    this._environment = config.environment || 'unknown';
    this.enabled = config.enabled ?? true;
    if (!this.enabled || !config.connectionString) {
      this.initialized = true;
      return;
    }

    const ottoEnv = getOttoEnv() || 'prod';
    const ottoRegion = getOttoRegion() || 'unknown';
    // Capture build identity ONCE at init so we don't re-walk script tags
    // on every event. The bundle hash, build id, package version, and
    // image/deploy tag never change for the lifetime of a loaded SPA.
    const buildInfo = getBuildInfo();

    this.appInsights = new ApplicationInsights({
      config: {
        connectionString: config.connectionString,
        enableAutoRouteTracking: false, // we handle routing manually
        enableCorsCorrelation: true,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        enableUnhandledPromiseRejectionTracking: true,
        enableAjaxPerfTracking: true,
        disableFetchTracking: false,
        samplingPercentage: config.samplingPercentage ?? 100,
        maxBatchInterval: 5000,
        disableExceptionTracking: false,
      },
    });

    this.appInsights.loadAppInsights();
    this.appInsights.addTelemetryInitializer((envelope) => {
      // Tag every event with the deployment environment + region so a single
      // Kusto query can pivot across all 10 ACAs:
      //   cloud_RoleName     = otto-portal-{staging|prod|canary}
      //   cloud_RoleInstance = {westus|eastasia|swedencentral|...}
      envelope.tags = envelope.tags || {};
      envelope.tags['ai.cloud.role'] = `otto-portal-${ottoEnv}`;
      envelope.tags['ai.cloud.roleInstance'] = ottoRegion;

      // Sanitize all custom properties and inject env + region. Note the
      // three sources are intentionally distinct:
      //   - `environment`  = Vite BUILD mode (production/canary/development),
      //                      baked at compile time from __BUILD_ENVIRONMENT__
      //   - `otto.env`     = DEPLOYMENT slot (staging/prod/canary) from the
      //                      ACA env var, ground-truth identity of the ACA
      //   - `otto.region`  = DEPLOYMENT region (westus/eastasia/...) from
      //                      the ACA env var
      // We duplicate otto.env/otto.region into properties (in addition to
      // tags) because some AI query surfaces (Logs blade, Workbooks)
      // expose customDimensions more conveniently than cloud_RoleName.
      if (envelope.data) {
        const data = envelope.data as ICustomProperties;
        if (data.baseData) {
          data.baseData.properties = {
            ...sanitizeProperties(data.baseData.properties) ?? {},
            environment: this._environment,
            'otto.env': ottoEnv,
            'otto.region': ottoRegion,
            // Build-identity fields. During an Ev2 rollout, AFD load-balances
            // requests across old + new ACA revisions in the same region, so
            // two users with identical otto.env + otto.region can be running
            // different builds at the same instant. These let a single Kusto
            // query pivot per-build / per-bundle:
            //   - otto.buildId       = ADO BUILD_BUILDID (e.g. "167562455")
            //   - otto.version       = versions.xml filever (e.g. "1.208.0.0")
            //   - otto.deployVersion = container IMAGE_TAG when set, else
            //                          falls back to buildId — identifies the
            //                          deployed artifact end-to-end
            //   - otto.bundleHash    = Vite content hash of the entry chunk
            //                          (e.g. "BWQ8MtsE"); matches the URL hash
            //                          in the CDN/AFD access logs so we can
            //                          correlate user events with edge logs.
            'otto.buildId': buildInfo.buildId,
            'otto.version': buildInfo.version,
            'otto.deployVersion': buildInfo.deployVersion,
            ...(buildInfo.bundleHash && { 'otto.bundleHash': buildInfo.bundleHash }),
          };
        }
      }
      return true;
    });

    this.initialized = true;
  }

  setAuthenticatedUser(userId: string, accountId?: string): void {
    this.appInsights?.setAuthenticatedUserContext(userId, accountId, true);
  }

  clearAuthenticatedUser(): void {
    this.appInsights?.clearAuthenticatedUserContext();
  }

  trackEvent(name: string, properties?: Record<string, unknown>, measurements?: Record<string, number>): void {
    if (!this.appInsights || !this.userTelemetryAllowed()) return;
    this.appInsights.trackEvent(
      { name, ...(measurements && { measurements }) },
      sanitizeProperties(properties),
    );
  }

  trackPageView(name: string, uri?: string, properties?: Record<string, unknown>): void {
    if (!this.appInsights || !this.userTelemetryAllowed()) return;
    const sanitized = sanitizeProperties(properties);
    this.appInsights.trackPageView({
      name,
      ...(uri && { uri }),
      ...(sanitized && { properties: sanitized }),
    });
  }

  trackException(error: Error | unknown, properties?: Record<string, unknown>): void {
    if (!this.appInsights || !this.userCrashReportsAllowed()) return;
    const exception = error instanceof Error ? error : new Error(String(error));
    const sanitized = sanitizeProperties(properties);
    this.appInsights.trackException({
      exception,
      severityLevel: SeverityLevel.Error,
      ...(sanitized && { properties: sanitized }),
    });
  }

  trackMetric(name: string, average: number, properties?: Record<string, unknown>): void {
    if (!this.appInsights || !this.userTelemetryAllowed()) return;
    this.appInsights.trackMetric({ name, average }, sanitizeProperties(properties));
  }

  trackTrace(message: string, severityLevel: SeverityLevel = SeverityLevel.Information, properties?: Record<string, unknown>): void {
    if (!this.appInsights || !this.userTelemetryAllowed()) return;
    const sanitized = sanitizeProperties(properties);
    this.appInsights.trackTrace({
      message: sanitizeValue(message),
      severityLevel,
      ...(sanitized && { properties: sanitized }),
    });
  }

  startTrackEvent(name: string): void {
    if (!this.userTelemetryAllowed()) return;
    this.appInsights?.startTrackEvent(name);
  }

  stopTrackEvent(name: string, properties?: Record<string, unknown>, measurements?: Record<string, number>): void {
    if (!this.userTelemetryAllowed()) return;
    this.appInsights?.stopTrackEvent(name, sanitizeProperties(properties), measurements);
  }

  flush(): void {
    this.appInsights?.flush();
  }

  get isInitialized(): boolean {
    return this.initialized;
  }

  get isEnabled(): boolean {
    return this.enabled && this.appInsights !== null;
  }

  get environment(): string {
    return this._environment;
  }
}

export const telemetryService = new TelemetryService();
export { SeverityLevel };
export type { TelemetryConfig };
